import { ref, Ref } from "vue";

import { WebAudioEngine } from "@readium/navigator";
import { Link, Publication, Locator } from "@readium/shared";

import { captureException, captureMessage } from "@sentry/browser";

import GuidedNavigationManager, {
  UpdateTimeResult,
} from "./GuidedNavigationManager";

import { mediaOverlayResource } from "@/helpers/constants";
import { assertNever } from "../../../modules/shared-utils/assertions";

import MediaPlayer, {
  MediaPlayerCommand,
  MediaPlayerState,
} from "../media/player";

export enum Direction {
  Previous = -1,
  Next = 1,
}

/**
 * GuidedNavigationService manages the guided navigation functionality
 *  - It handles the creation of the guided navigation object, the audio player,
 *  - and the interaction with the publication and the user interface.
 *  - It also manages the audio playback,
 *  - highlighting of elements,
 *  - and the navigation between different audio resources.
 */
export class GuidedNavigationService

  // TODO: consider the use of a polyfill for EventTarget extension
  // would be useful for pre- Safari 14
  // see: https://github.com/benlesh/event-target-polyfill/blob/master/index.js
  extends EventTarget
  implements MediaPlayer
{
  private readonly _state: Ref<MediaPlayerState | "pageChanging" | "pageEnd"> =
    ref(MediaPlayerState.Stopped);
  private readonly hasPrevious: Ref<boolean> = ref(false);
  private readonly hasNext: Ref<boolean> = ref(false);

  private readonly audioContext = new AudioContext();
  private readonly audioPlayer = new WebAudioEngine({
    audioContext: this.audioContext,
    playback: {
      state: {
        currentTime: 0,
        duration: 0,
        volume: 0.5,
      },
      playWhenReady: true,
      index: 1,
    },
  });
  private readonly managers: Record<string, GuidedNavigationManager> = {};
  private activeManager: GuidedNavigationManager | null = null;

  private readonly guidedLinks: Link[];

  constructor(private readonly publication: Publication) {
    super();

    this.guidedLinks = this.publication.manifest.readingOrder.items.filter(
      (link) => {
        return !!link.alternates?.findWithMediaType(mediaOverlayResource.name);
      }
    );

    this.audioPlayer.on("timeupdate", this.handleAudioTimeUpdate);
    this.audioPlayer.on("ended", this.handleAudioEnded);
    this.audioPlayer.on("error", this.handleAudioError);
  }

  public dispose(): void {
    this.stop();

    this.audioPlayer.off("timeupdate", this.handleAudioTimeUpdate);
    this.audioPlayer.off("ended", this.handleAudioEnded);
    this.audioPlayer.off("error", this.handleAudioError);

    // @ts-expect-error private access is the only way to ensure the media element is removed
    const mediaElement = this.audioPlayer.mediaElement as HTMLMediaElement;
    mediaElement?.remove();
  }

  public get state(): MediaPlayerState {
    switch (this._state.value) {
      case "pageChanging":
        return MediaPlayerState.Playing;
      case "pageEnd":
        return MediaPlayerState.Paused;
      default:
        return this._state.value;
    }
  }

  public async startAtLocator(locator: Locator): Promise<void> {
    this.stop();

    const manager = await this.managerFor(locator.href);
    // As there's no guided navigation for this locator, we need to jump to the next resource in the reading order searching for a guided navigation
    if (!manager) {
      const nextLocator = this.findNextLocator(locator.href, Direction.Next);
      if (nextLocator) {
        return this.startAtLocator(
          new Locator({ href: nextLocator, type: locator.type })
        );
      }
      return;
    }

    await this.start(manager, 0);
  }

  public async startAtElement(href: string, id: string): Promise<void> {
    this.stop();

    const manager = await this.managerFor(href);
    if (!manager) return;

    const element = manager.guided.findIndex((item) => item.fragmentId === id);
    if (element === -1) return;

    await this.start(manager, element);
  }

  private async start(
    manager: GuidedNavigationManager,
    index: number
  ): Promise<void> {
    this.stop();

    this.emit("pageStart", { href: manager.href });

    await new Promise<void>((resolve, reject) => {
      this.activeManager = manager.activate(index, resolve, reject);
    });

    this._state.value = MediaPlayerState.Playing;
    await this.activeManager.start(index);
  }

  private async continueTo(href: string, index: number = 0): Promise<void> {
    this._state.value = "pageChanging";

    try {
      // pause the player, but don't change our own state
      this.audioPlayer.pause();

      const nextManager = await this.managerFor(href);
      if (!nextManager) {
        this.stop();
      }

      if (index < 0) {
        index = nextManager.guided.length + index;
      }

      if (index < 0 || index >= nextManager.guided.length) {
        index = 0;
      }

      this.emit("pageStart", { href });

      await new Promise<void>((resolve, reject) => {
        this.activeManager = nextManager.activate(index, resolve, reject);
      });

      if (await this.activeManager.start(index)) {
        this._state.value = MediaPlayerState.Playing;
      } else {
        this.stop();
      }
    } catch (error) {
      this.stop();
      throw error;
    }
  }

  // this method must work on reactive data only
  can(command: MediaPlayerCommand): boolean {
    switch (command) {
      case "resume":
        return this.state === MediaPlayerState.Paused;
      case "pause":
        // specifically not "pageChanging"
        return this._state.value === MediaPlayerState.Playing;
      case "previous":
        return (
          this.state !== MediaPlayerState.Stopped && this.hasPrevious.value
        );
      case "next":
        return this.state !== MediaPlayerState.Stopped && this.hasNext.value;
      case "stop":
        return this.state !== MediaPlayerState.Stopped;
      default:
        assertNever(command);
    }
  }
  pause(): void {
    if (this._state.value !== MediaPlayerState.Playing) return;

    this._state.value = MediaPlayerState.Paused;
    this.audioPlayer.pause();
  }
  async resume(): Promise<void> {
    if (this._state.value === MediaPlayerState.Paused) {
      this._state.value = MediaPlayerState.Playing;
      try {
        await this.audioPlayer.play();
      } catch (error) {
        this.stop();
        throw error;
      }
    } else if (this._state.value === "pageEnd") {
      const next = this.findRelative(this.activeManager.href, Direction.Next);
      await this.continueTo(next);
    }
  }
  stop(): void {
    this._state.value = MediaPlayerState.Stopped;
    this.emit("highlight", []);
    this.activeManager?.stop();
    this.activeManager = null;
    this.audioPlayer.stop();
  }
  async previous(): Promise<void> {
    await this.go(Direction.Previous);
  }
  async next(): Promise<void> {
    await this.go(Direction.Next);
  }

  private async go(direction: Direction): Promise<void> {
    if (this.state === MediaPlayerState.Stopped) return;

    const manager = this.activeManager;
    if (!manager) return;

    if (await manager.go(direction)) return;

    const origin = this.activeManager.href;
    const target = this.findRelative(origin, direction);

    if (target) {
      return await this.continueTo(target, direction);
    }

    if (direction === Direction.Previous) {
      // probably first word of the whole book, restart there
      if (await this.activeManager.start(0)) {
        return;
      }
    }

    // failed to play or at the end of the book
    this.stop();
  }

  public async managerFor(href: string): Promise<GuidedNavigationManager> {
    if (this.managers[href]) {
      return this.managers[href];
    }

    const link = Link.deserialize({ href });

    try {
      const document = await this.publication.guideForLink(link);
      if (!document) return null;

      return (this.managers[href] = new GuidedNavigationManager(
        this.publication,
        this.audioPlayer,
        href,
        document
      ));
    } catch (error) {
      console.error("Error creating guided navigation manager. ", error);
      throw error;
    }
  }

  private emit(type: string, data: any): boolean {
    const event = new CustomEvent(type, { detail: data, cancelable: true });
    return this.dispatchEvent(event);
  }

  public setAudioVolume(volume: number) {
    this.audioPlayer.setVolume(volume);
  }

  /**
   * Handle the player time update event. Check the current time received, play the audio associated and
   * highlight the related elements in the dom.
   * @param currentTime
   * @returns
   */
  private handleAudioTimeUpdate = async (currentTime: number) => {
    const manager = this.activeManager;

    // when the audio file load, the time is set to 0
    if (!manager || this.state !== MediaPlayerState.Playing) return;

    // don't trigger anything while the page change is in progress
    if (this._state.value === "pageChanging") return;

    const previousActiveId = manager.activeId;
    const result = manager.updateTime(currentTime);

    this.hasPrevious.value =
      manager.hasPrevious ||
      this.findRelative(manager.href, Direction.Previous) !== null;
    this.hasNext.value =
      manager.hasNext ||
      this.findRelative(manager.href, Direction.Next) !== null;

    switch (result) {
      case UpdateTimeResult.NoClips:
        // TODO: we should probably handle a page change here?
        console.warn("No clips found");
        return;
      case UpdateTimeResult.OnClip: {
        const newActiveId = this.activeManager.activeId;
        const prevClipIndex = manager.getClipIndex(previousActiveId);
        const newClipIndex = manager.getClipIndex(newActiveId);
        const prevAudioHref = manager.clips[prevClipIndex]?.audioResource;
        const newAudioHref = manager.clips[newClipIndex]?.audioResource;

        // If the audio resource reference has changed in between clips,
        // we need to add a warning for future reference.
        if (prevAudioHref && prevAudioHref !== newAudioHref) {
          captureMessage(
            "Multiple audio references in the same guided navigation manager",
            {
              extra: {
                publication: this.publication.manifest.metadata.identifier,
              },
              level: "warning",
            }
          );
        }

        if (previousActiveId !== newActiveId) {
          if (
            prevClipIndex === -1 ||
            Math.abs(newClipIndex - prevClipIndex) === 1
          ) {
            // highlight only the new active clip
            this.emit("highlight", [
              {
                href: manager.href,
                id: newActiveId,
              },
            ]);
          } else {
            // highlight all clips between the previous and new active clip, if they are not adjacent
            const tempHighlightElements = [];
            for (
              let clipIndex = prevClipIndex;
              clipIndex <= newClipIndex;
              clipIndex++
            ) {
              tempHighlightElements.push({
                href: manager.href,
                id: manager.clips[clipIndex].fragmentId,
              });
            }
            this.emit("highlight", tempHighlightElements);
          }
        }
        return;
      }
      case UpdateTimeResult.Beginning:
      case UpdateTimeResult.BetweenClips:
        // nothing to do, time will progress to the next clip
        return;
      case UpdateTimeResult.End: {
        this.handleUtteranceEnd();
        return;
      }
      default:
        assertNever(result);
    }
  };

  private findRelative(href: string, dir: -1 | 1): string | null {
    const index = this.guidedLinks.findIndex((link) => link.href === href);
    if (index === -1) return null;

    // TODO: is there rel=prev/next properties sometimes? is ordering guaranteed?
    return this.guidedLinks[index + dir]?.href || null;
  }

  /**
   * Find the adjacent locator href in the reading order, given a direction.
   * @param href The current href to start searching from.
   * @param direction The direction to search in (next or previous).
   */
  private findNextLocator(href: string, direction: Direction): string | null {
    const index = this.publication.readingOrder.items.findIndex(
      (item) => item.href === href
    );
    if (index === -1) return null;
    const elementInReadingOrder = this.publication.readingOrder.items[index];

    const guidedIndex = this.guidedLinks.findIndex(
      (link) => link.href === elementInReadingOrder.href
    );
    // Search for the next locator in the specified direction
    const nextIndex = index + direction;
    if (
      nextIndex < 0 ||
      nextIndex >= this.publication.readingOrder.items.length
    ) {
      return null; // No next locator in the specified direction
    }

    if (guidedIndex === -1) {
      const nextElementInReadingOrder =
        this.publication.readingOrder.items[nextIndex];
      return this.findNextLocator(nextElementInReadingOrder.href, direction);
    }
    return this.guidedLinks[guidedIndex].href;
  }

  /**
   * Handle the audio ended event. If the current audio is the last resource, stop the audio player.
   * If not, go to the next or previous audio resource depending on the direction.
   */
  private handleAudioEnded = async () => {
    this.handleUtteranceEnd();
  };

  private handleAudioError = (error: Error) => {
    console.error("Audio error: ", error);
    captureException(error);
  };

  private handleUtteranceEnd = async () => {
    const manager = this.activeManager;
    if (!manager) return;
    const next = this.findRelative(manager.href, Direction.Next);
    const changePage = this.emit("pageEnd", {
      current: manager.href,
      next,
    });
    if (!next) {
      this.stop();
    } else if (changePage) {
      await this.continueTo(next);
    } else {
      this._state.value = "pageEnd";
      this.audioPlayer.pause();
    }
  };
}
